
import { useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import YearSelector from "@/components/YearSelector";
import { User, BirdieRecord } from "@/types";
import { useBirdieData } from "@/hooks/useBirdieData";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { Trophy } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";

interface BirdieBoardProps {
  users: User[];
  birdieRecords: BirdieRecord[];
  initialYear?: number;
  onYearChange?: (year: number) => void;
  isLoading?: boolean;
  showSampleToggle?: boolean;
  showSampleData?: boolean;
  onToggleSampleData?: (value: boolean) => void;
}

const BirdieBoard = ({
  users,
  birdieRecords,
  initialYear = new Date().getFullYear(),
  onYearChange,
  isLoading = false,
  showSampleToggle = false,
  showSampleData = false,
  onToggleSampleData,
}: BirdieBoardProps) => {
  const [selectedYear, setSelectedYear] = useState(initialYear);

  // Create stable keys for memoization
  const stableUserIds = useMemo(() =>
    users.map(u => u.id).sort(),
    [users]
  );

  const stableRecordIds = useMemo(() =>
    birdieRecords.map(r => r.id).sort(),
    [birdieRecords]
  );

  // Now create memoized arrays using the stable keys as dependencies
  const memoizedUsers = useMemo(() => users, [stableUserIds]);
  const memoizedRecords = useMemo(() => birdieRecords, [stableRecordIds]);

  // Compute the earliest year with a birdie record
  const minYear = useMemo(() => {
    if (birdieRecords.length === 0) return undefined;
    let earliest = Infinity;
    for (const record of birdieRecords) {
      const year = new Date(record.date).getFullYear();
      if (year < earliest) earliest = year;
    }
    return earliest;
  }, [stableRecordIds]);

  const { getBirdieLeaders } = useBirdieData(
    memoizedUsers,
    memoizedRecords,
    selectedYear
  );

  // Only show users who have at least 1 birdie, with tied ranks
  const rankedLeaders = useMemo(() => {
    const allLeaders = getBirdieLeaders();
    const withBirdies = allLeaders.filter(leader => leader.birdieCount > 0);

    // Assign tied ranks: players with the same count share the same rank
    let rank = 1;
    return withBirdies.map((leader, index) => {
      if (index > 0 && leader.birdieCount < withBirdies[index - 1].birdieCount) {
        rank = index + 1;
      }
      return { ...leader, rank };
    });
  }, [getBirdieLeaders, selectedYear]);

  const handleYearChange = useCallback((year: number) => {
    setSelectedYear(year);
    if (onYearChange) {
      onYearChange(year);
    }
  }, [onYearChange]);

  const getRankBadgeClass = (rank: number) => {
    if (rank === 1) return "bg-gold text-white";
    if (rank === 2) return "bg-muted-foreground/60 text-white";
    return "bg-gold/50 text-white";
  };

  return (
    <div className="px-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-semibold text-forest">Birdie Board</h2>
        {showSampleToggle && (
          <div className="flex items-center gap-2">
            <Label htmlFor="sample-toggle" className="text-xs text-muted-foreground cursor-pointer">
              Show Sample Data
            </Label>
            <Switch
              id="sample-toggle"
              checked={showSampleData}
              onCheckedChange={onToggleSampleData}
              className="scale-75 origin-right"
            />
          </div>
        )}
      </div>

      <YearSelector
        currentYear={selectedYear}
        onYearChange={handleYearChange}
        minYear={minYear}
      />

      <Card className="overflow-hidden border border-border/50 shadow-sm rounded-xl mt-3">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {Array(3).fill(0).map((_, index) => (
                <div key={`skeleton-${index}`} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-7 w-7 rounded-full" />
                    <Skeleton className="h-9 w-9 rounded-full" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-6 w-14 rounded-full" />
                </div>
              ))}
            </div>
          ) : rankedLeaders.length === 0 ? (
            <EmptyState
              icon={Trophy}
              title={`No birdies recorded for ${selectedYear}`}
              description="Record birdies to see the leaderboard"
              className="py-6"
            />
          ) : (
            <div className="divide-y divide-border/50">
              {rankedLeaders.map((leader) => (
                <div
                  key={leader.id}
                  className="flex items-center justify-between px-4 py-3"
                >
                  <div className="flex items-center gap-3">
                    <Badge
                      className={cn(
                        "w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold shrink-0",
                        getRankBadgeClass(leader.rank)
                      )}
                    >
                      {leader.rank}
                    </Badge>
                    <AvatarPlaceholder
                      user={leader}
                      size="md"
                    />
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">{leader.name}</span>
                      {leader.status && leader.status !== 'accepted' && (
                        <span className="text-[11px] text-muted-foreground">
                          {leader.status === 'invited' ? 'Invited' :
                           leader.status === 'pending' ? 'Pending' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-forest tabular-nums">
                    {leader.birdieCount}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
        <CardFooter className="px-4 py-3 bg-muted/30 border-t border-border/50">
          <Link to="/how-to-play" className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors">
            <p>Birdies count when all players are present. No mulligans, no gimmes. <span className="text-gold font-medium">Learn more</span></p>
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
};

export default BirdieBoard;
